/**
 * PoseGuard — vetoes physically impossible pose jumps.
 *
 * AlvaAR will report a confident pose that has teleported tens of units between
 * two consecutive 60 ms frames, and reports `tracking` the whole time so nothing
 * downstream can tell. Measured on a real Android capture (2026-08-13):
 *
 *   median step 0.0717 units
 *   24 steps of ~43 units, carrying 89.3% of the total reported path
 *
 * The ~43 figure repeats almost exactly, which is the signature of a tracker
 * flip-flopping between two duplicate map regions it never merged rather than of
 * drift.
 *
 * One such pose poisons everything downstream: the floor estimator sees camera
 * height jump, never gets its 8 agreeing samples, and never locks — which is why
 * floor raycasting had never once activated in the field, and why fragments
 * appeared to float rather than stick.
 *
 * The test is RELATIVE — a multiple of the recent step distribution — because
 * monocular map scale is arbitrary. A threshold expressed in "units" is
 * meaningless when a unit may be a centimetre in one session and a metre in the
 * next; the same absolute number would veto everything or nothing.
 *
 * Lives apart from AlvaARBackend so it can be tested without WASM or a device.
 */

/** Accepted step magnitudes kept as the reference distribution. */
const HISTORY = 40;
/** Need this many before the guard is willing to judge. */
const MIN_HISTORY = 12;
/** Reject a step beyond p90(history) x this. Real teleports were ~600x median. */
const FACTOR = 8;
/**
 * After this many consecutive rejections, accept anyway and re-seed.
 *
 * Without it the guard could deadlock: if the map legitimately re-scales, or the
 * participant genuinely accelerates, every later pose looks like a jump against a
 * now-stale reference and tracking never recovers. The cost of being wrong is
 * bounded — a real teleport gets through ~250 ms late instead of never.
 */
const MAX_CONSECUTIVE = 4;

export class PoseGuard {
  private history: number[] = [];
  private run = 0;
  private rejected = 0;
  private invalid = 0;

  /** Consecutive rejections right now. >0 means the map is contradicting itself. */
  get consecutiveRejections(): number {
    return this.run;
  }

  /** Total vetoed poses this session. */
  get totalRejected(): number {
    return this.rejected;
  }

  /** Non-finite steps refused this session (a NaN/Infinity pose from the tracker). */
  get totalInvalid(): number {
    return this.invalid;
  }

  /** Reference step size (p90 of recent accepted steps), or null if not enough data. */
  get reference(): number | null {
    if (this.history.length < MIN_HISTORY) return null;
    const sorted = [...this.history].sort((a, b) => a - b);
    return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.9))]!;
  }

  /**
   * @param distance magnitude of the proposed step from the last accepted pose
   * @param settled  whether we already have a trustworthy previous pose; when
   *                 false the guard always accepts a FINITE step, since there is
   *                 nothing to compare against and refusing would prevent
   *                 initialisation
   *
   * ⚠️ A non-finite distance is ALWAYS refused, settled or not. It used to be
   * accepted ("garbage must not poison the reference"), which let a NaN pose
   * become the backend's position — and every anchor created afterwards froze
   * a NaN origin, permanently. Refusing it neither advances nor clears the
   * rejection run: a NaN frame is not evidence either way.
   */
  accept(distance: number, settled = true): boolean {
    if (!Number.isFinite(distance)) {
      this.invalid++;
      return false;
    }
    if (!settled) {
      this.run = 0;
      return true;
    }

    const ref = this.reference;
    if (ref !== null && ref > 0 && distance > ref * FACTOR) {
      this.run++;
      this.rejected++;
      if (this.run <= MAX_CONSECUTIVE) return false;
      // Persistent disagreement: the map really has moved under us.
      this.history = [];
      this.run = 0;
      return true;
    }

    this.run = 0;
    this.history.push(distance);
    if (this.history.length > HISTORY) this.history.shift();
    return true;
  }

  reset(): void {
    this.history = [];
    this.run = 0;
    this.rejected = 0;
    this.invalid = 0;
  }
}

/** What the backend should do with one tracker result. */
export type PoseVerdict =
  /** Believe it: becomes the current pose, state 'tracking'. */
  | 'accept'
  /** A teleport: hold the last good pose, report 'lost', keep the guard armed. */
  | 'veto'
  /** Non-finite: hold the last good pose, report 'lost'/'initializing'. */
  | 'invalid';

/**
 * The bookkeeping around `PoseGuard` — WHEN it is allowed to judge.
 *
 * ## The bug this exists for
 *
 * The guard needs to know whether the previous pose is trustworthy
 * (`settled`). The backend used to derive that as
 * `everTracked && state === 'tracking'` — but a veto itself sets the state to
 * 'lost'. So the frame after a veto was judged UNSETTLED, accepted
 * unconditionally, and the rejection run reset to 0. `MAX_CONSECUTIVE` could
 * never be reached, and the guard delayed every teleport by exactly one frame
 * instead of blocking it. It also made the mapHealth "slow down" hint, which
 * keys on `consecutiveRejections > 0`, effectively unreachable.
 *
 * The distinction that matters is WHY the backend is not tracking:
 *
 *  - **vetoed** — the tracker DID return a pose, we refused it. The last
 *    accepted pose is still the right reference; keep judging against it.
 *  - **genuinely lost** — the tracker returned nothing. Whatever it returns next
 *    is a relocalisation, which can legitimately be anywhere in the map, so it
 *    is accepted unconditionally (as it always was).
 *
 * Lives here, not in the backend, so the test drives the real logic rather
 * than a hand copy of it.
 */
export class PoseAcceptance {
  readonly guard: PoseGuard;
  private tracked = false;
  private lastAccepted = false;
  private vetoPending = false;

  constructor(guard: PoseGuard = new PoseGuard()) {
    this.guard = guard;
  }

  /** Whether any pose has been accepted since the last reset. */
  get everTracked(): boolean {
    return this.tracked;
  }

  /** True while the most recent tracker pose was vetoed (and not superseded). */
  get vetoActive(): boolean {
    return this.vetoPending;
  }

  get consecutiveRejections(): number {
    return this.guard.consecutiveRejections;
  }

  /**
   * Judge a pose the tracker returned.
   *
   * @param distance step from the last ACCEPTED position (not from a vetoed one)
   * @param poseFinite whether every element of the pose was finite; a NaN
   *   rotation with a finite translation is just as poisonous as a NaN step
   */
  judge(distance: number, poseFinite = true): PoseVerdict {
    if (!poseFinite || !Number.isFinite(distance)) {
      // Count it, but leave settledness exactly as it was: a garbage frame is
      // not evidence that the map is fine, nor that it was genuinely lost.
      this.guard.accept(NaN);
      return 'invalid';
    }
    const settled = this.tracked && (this.lastAccepted || this.vetoPending);
    if (this.guard.accept(distance, settled)) {
      this.tracked = true;
      this.lastAccepted = true;
      this.vetoPending = false;
      return 'accept';
    }
    this.lastAccepted = false;
    this.vetoPending = true;
    return 'veto';
  }

  /**
   * The tracker returned no pose at all — still initialising, or genuinely
   * lost. Disarms the guard: the next pose is a relocalisation.
   */
  noPose(): void {
    this.lastAccepted = false;
    this.vetoPending = false;
  }

  reset(): void {
    this.guard.reset();
    this.tracked = false;
    this.lastAccepted = false;
    this.vetoPending = false;
  }
}
